import {computed, inject, Injectable, signal} from '@angular/core';
import {NzI18nService, en_US, zh_CN} from 'ng-zorro-antd/i18n';
import {TranslocoService} from '@jsverse/transloco';
import {StorageService} from '../../services/storage.service';
import {AppLanguage, AVAILABLE_LANGS, I18N_STORAGE_KEY} from './i18n.config';
import {firstValueFrom} from 'rxjs';

const LEGACY_TO_REGION_LANG_MAP = new Map<string, AppLanguage>([
  ['en', 'en-US'],
  ['zh', 'zh-CN'],
]);

@Injectable({providedIn: 'root'})
export class I18nService {
  private readonly storageService = inject(StorageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly nzI18nService = inject(NzI18nService);

  readonly activeLanguage = signal<AppLanguage>('en-US');
  readonly activeLanguageLabel = computed(() => this.activeLanguage() === 'zh-CN' ? '简体中文' : 'English');

  async init(): Promise<void> {
    const storedLanguage = await this.storageService.lfGet(I18N_STORAGE_KEY);
    const browserLanguage = typeof navigator === 'undefined' ? null : navigator.language;
    const language = this.normalizeLanguage(typeof storedLanguage === 'string' ? storedLanguage : browserLanguage);

    await this.applyLanguage(language, false);
  }

  async setLanguage(language: string): Promise<void> {
    const normalized = this.normalizeLanguage(language);
    await this.applyLanguage(normalized, true);
  }

  private async applyLanguage(language: AppLanguage, persist: boolean): Promise<void> {
    await firstValueFrom(this.translocoService.load(language));
    this.activeLanguage.set(language);
    this.translocoService.setActiveLang(language);
    this.nzI18nService.setLocale(language === 'zh-CN' ? zh_CN : en_US);

    if (persist) {
      await this.storageService.lfSet(I18N_STORAGE_KEY, language);
    }
  }

  private normalizeLanguage(language: string | null | undefined): AppLanguage {
    const normalized = (language ?? 'en-US').trim();
    if ((AVAILABLE_LANGS as readonly string[]).includes(normalized)) {
      return normalized as AppLanguage;
    }

    const lower = normalized.toLowerCase();
    if (LEGACY_TO_REGION_LANG_MAP.has(lower)) {
      return LEGACY_TO_REGION_LANG_MAP.get(lower) ?? 'en-US';
    }

    if (lower.startsWith('zh')) {
      return 'zh-CN';
    }

    return 'en-US';
  }
}
